import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import {
  DrawerRoot,
  DrawerTrigger,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  DrawerDismiss,
} from '@mentimeter/ragnar-ui/drawer';
import { Text } from '@mentimeter/ragnar-ui/text';
import {
  CommentsPaneDataHandler,
  FOOTER_ACTIONS_HEIGHT_MOBILE,
} from '@mentimeter/editor-properties-pane';
import { useSetPaneWithTracking } from 'features/core/editor/Sidebar/utils';
import type { TrackingContext } from '@mentimeter/http-clients';
import { ChevronDownIcon, ChevronUpIcon } from '@mentimeter/ragnar-visuals';
import { Clickable } from '@mentimeter/ragnar-ui/clickable';
import { Box } from '@mentimeter/ragnar-ui/box';
import { IconButton } from '@mentimeter/ragnar-ui/icon-button';
import { EDITOR_MOBILE_LAYOUT_ACTION_PANE_ID } from 'app/(tracked)/[seriesId]/(collaborator)/(editor)/editor-layout-components/EditorMobileLayout';

export const ViewOnlyCommentDrawer = ({
  open,
  isShared,
  currentPlan,
  roleInWorkspace,
  trackingContext,
}: {
  open: boolean;
  isShared: boolean;
  currentPlan: string;
  roleInWorkspace: string;
  trackingContext: TrackingContext;
}) => {
  const t = useTranslations('creation');
  const [topOffset, setTopOffset] = useState(0);

  useEffect(() => {
    const pane = document.getElementById(EDITOR_MOBILE_LAYOUT_ACTION_PANE_ID);
    if (!pane) return;

    const offset = pane.getBoundingClientRect().top;
    setTopOffset(offset);
  }, [open]);

  const setPaneWithTracking = useSetPaneWithTracking(
    false,
    isShared,
    currentPlan,
    roleInWorkspace,
    trackingContext,
  );

  const drawerTitle = t('editor_sidebar.sidebar_comments_label');

  return (
    <DrawerRoot
      open={open}
      onOpenChange={() => setPaneWithTracking(open ? 'closed' : 'comments')}
    >
      <DrawerTrigger>
        <Clickable
          height={FOOTER_ACTIONS_HEIGHT_MOBILE}
          bg="bg"
          justifyContent="space-between"
          width="100%"
          flexDirection="row"
          pl="space8"
          pr="space4"
          alignItems="center"
          onClick={() => setPaneWithTracking(open ? 'closed' : 'comments')}
        >
          <Text fontSize="112.5">{drawerTitle}</Text>
          <Box
            extend={({ theme }) => ({
              transition: `transform ${theme.durations[1]}s ease`,
              transform: `rotate(${open ? 180 : 0}deg)`,
            })}
          >
            <ChevronUpIcon />
          </Box>
        </Clickable>
      </DrawerTrigger>
      <DrawerOverlay className="overflow-hidden bg-transparent">
        <DrawerContent
          className="w-full"
          style={{
            position: 'absolute',
            top: `${topOffset}px`,
            height: `calc(100dvh - ${topOffset}px)`,
          }}
        >
          <Box
            pl="space8"
            pr="space4"
            py="space8"
            alignContent="center"
            flexDirection="row"
            alignItems="flex-end"
            justifyContent="space-between"
          >
            <Text fontSize="112.5">{drawerTitle}</Text>
            <DrawerDismiss>
              <IconButton
                variant="secondary"
                aria-label={t(
                  'comments.comments_view_only_drawer_close_menu_label_a11y',
                )}
                onClick={() =>
                  setPaneWithTracking(open ? 'closed' : 'comments')
                }
                borderRadius="full"
              >
                <ChevronDownIcon aria-hidden />
              </IconButton>
            </DrawerDismiss>
          </Box>

          <DrawerBody className="overflow-hidden">
            <CommentsPaneDataHandler
              trackingContext={trackingContext}
              visible={open}
            />
          </DrawerBody>
        </DrawerContent>
      </DrawerOverlay>
    </DrawerRoot>
  );
};
